import { useSearchParams } from 'react-router-dom';
import { Pagination } from '@/features/common/Pagination';
import { NewsCardLink } from '@/features/publicsite/NewsCardLink';
import { NewsFilterPanel } from '@/features/publicsite/news/NewsFilterPanel';
import {
  activeNewsFilterCount,
  newsPageHref,
  withNewsFilters,
} from '@/features/publicsite/news/newsFilters';
import { useNewsCategories } from '@/features/publicsite/news/useNewsCategories';
import { useNewsListing } from '@/features/publicsite/news/useNewsListing';
import type { NewsFilterName } from '@/features/publicsite/types/NewsFilterName';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicNews } from '@/i18n/dictionaries/publicNews';
import { useTranslation } from '@/i18n/useTranslation';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';

/**
 * /stiri — every published story, a page at a time, under the filter card.
 *
 * What the list is filtered by lives in the URL (news/newsFilters.ts); the categories and the page
 * of stories are loaded by their own hooks, in that order, as they always were.
 */
export function NewsPage() {
  const { t } = useTranslation(publicNews);
  usePublicPageTitle(t('heading'));
  const { language } = useLanguage();
  const [params, setParams] = useSearchParams();
  const page = Math.max(0, Number(params.get('page') ?? 0) || 0);
  const categories = useNewsCategories();
  const { data, fresh } = useNewsListing(params, page);
  const filtering = activeNewsFilterCount(params) > 0;

  // See withNewsFilters: several filters at once, and back to the first page. preventScrollReset:
  // every filter change is a navigation, and PublicShell's <ScrollRestoration> sent each one to the
  // top of the page — away from the filter just used, and on a phone out from under a period
  // panel that was still open.
  const setFilter = (changes: Partial<Record<NewsFilterName, string | null>>) =>
    setParams(withNewsFilters(params, changes), { preventScrollReset: true });

  // Real hrefs, not click handlers: a page number is a place, so it has to be openable in a new
  // tab, shareable, and reachable by a crawler — filters included, see newsPageHref.
  const hrefFor = (target: number) => newsPageHref(params, target);

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <section className="container section">
      <h1>{t('heading')}</h1>

      <NewsFilterPanel
        params={params}
        categories={categories}
        results={filtering && fresh && data ? t('resultsCount', { count: data.totalItems }) : ''}
        onFilter={setFilter}
        onReset={() => setParams(new URLSearchParams(), { preventScrollReset: true })}
      />

      {items.length > 0 && (
        <div className="card-grid" aria-busy={fresh ? undefined : true}>
          {items.map((item) => (
            <NewsCardLink
              key={item.slug}
              item={item}
              defaultCategory={t('defaultCategory')}
              language={language}
            />
          ))}
        </div>
      )}

      {fresh && data && items.length === 0 && (
        <p className="empty-state">{filtering ? t('filteredEmptyState') : t('emptyState')}</p>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        hrefFor={hrefFor}
        labels={{
          region: t('paginationAria'),
          previous: t('previous'),
          next: t('next'),
          page: (n) => t('pageAria', { page: n }),
          current: (n) => t('currentPageAria', { page: n }),
        }}
      />
    </section>
  );
}
